import { Flex, Spinner, Switch, Text } from '@chakra-ui/react'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'

import { Tooltip } from '../../../components/Tooltip'
import { useAppToast } from '../../../helpers'
import ScheduleClassService from '../../../services/ScheduleClasses/ScheduleClassService'

const SWITCH_ID = 'schedule-course-pages-switch'

interface ICoursePagesScheduleSwitchProps {
  /** edit_class_schedule: sem ela o estado é informação, não controle. */
  canEdit: boolean
}

/**
 * Liga/desliga da seção "Próximas Turmas" nas páginas de curso do site (ABRA-411).
 *
 * Grava sozinho, sem botão Salvar: desligado, a API passa a responder o cronograma de todo
 * curso com a lista vazia, e o site esconde a seção como faz com curso sem turma futura.
 * A página /cronograma-turmas não é afetada.
 */
export default function CoursePagesScheduleSwitch({ canEdit }: ICoursePagesScheduleSwitchProps) {
  const service = useMemo(() => new ScheduleClassService(), [])
  const toast = useAppToast()
  // Nulo enquanto carrega, ou se a leitura falhou: o switch fica trancado em vez de mostrar
  // um "ligado" que ninguém confirmou.
  const [isEnabled, setIsEnabled] = useState<boolean | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  // Valor exibido enquanto a gravação está em voo; zerado no `finally`, o que dispensa
  // rollback à mão no erro.
  const [optimistic, setOptimistic] = useState<boolean | null>(null)

  useEffect(() => {
    service
      .getSettings()
      .then((settings) => setIsEnabled(settings.show_on_course_pages))
      .catch((error) => toast.error('Erro ao carregar a exibição do cronograma', error))
      .finally(() => setIsLoading(false))
  }, [service, toast])

  const handleToggle = async (event: ChangeEvent<HTMLInputElement>) => {
    if (isSaving) return

    const next = event.target.checked
    setOptimistic(next)
    setIsSaving(true)
    try {
      const saved = await service.updateSettings({ show_on_course_pages: next })
      setIsEnabled(saved.show_on_course_pages)
      toast.success(
        saved.show_on_course_pages
          ? 'Cronograma exibido nas páginas de curso'
          : 'Cronograma oculto das páginas de curso'
      )
    } catch (error) {
      toast.error('Erro ao atualizar a exibição do cronograma', error)
    } finally {
      setOptimistic(null)
      setIsSaving(false)
    }
  }

  const isChecked = optimistic ?? isEnabled ?? false

  return (
    <Flex align="center" gap="2">
      <Tooltip
        label="Mostra ou esconde a seção Próximas Turmas em todas as páginas de curso do site. A página do cronograma não muda."
        maxW="260px"
        textAlign="center"
        // O `ref` do Switch vai para o input escondido: sem o span do `shouldWrapChildren` a
        // dica ancoraria num elemento de 1px fora do trilho (ver ArtworkShowcaseControl).
        shouldWrapChildren
      >
        <Switch
          id={SWITCH_ID}
          isChecked={isChecked}
          isDisabled={!canEdit || isEnabled === null || isSaving}
          onChange={handleToggle}
          display="flex"
        />
      </Tooltip>

      <Text as="label" htmlFor={SWITCH_ID} fontSize="sm" fontWeight="600" color="gray.700" whiteSpace="nowrap">
        Exibir nas páginas de curso
      </Text>

      {/* Também na leitura inicial: o switch trancado e cinza se confundiria com "desligado". */}
      {(isLoading || isSaving) && <Spinner size="xs" color="blue.500" />}
    </Flex>
  )
}
